import React, { useCallback, useEffect, useState } from 'react';
import '../admin.css';
import UserProfile from '../../Profile/UserProfile';
import { Card, Form, Input, Select, Button, message, Space, Tag, List, Modal, Typography } from 'antd';
import { API_BASE as apiBase, authHeaders } from '../../config/api';
const { Text } = Typography;

const Profile = () => {
  const [loading, setLoading] = useState(false);
  const [biz, setBiz] = useState(null);
  const [form] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [twoFAForm] = Form.useForm();
  const [twoFA, setTwoFA] = useState({ enabled: false, secret: null, backupCodes: [] });

  const loadBusiness = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/my/business-account`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json().catch(() => (null));
      const record = body && typeof body === 'object' ? body : null;
      setBiz(record);
      if (record) {
        form.setFieldsValue({
          company_name: record.company_name || '',
          industry: record.industry || '',
          website: record.website || '',
          business_email: record.business_email || '',
          contact: record.contact || '',
          address: record.address || '',
        });
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => { loadBusiness(); }, [loadBusiness]);

  const saveBusiness = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (biz && biz.id) {
        // Update existing (self-service)
        const res = await fetch(`${apiBase}/my/business-account`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(values)
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to update business details');
        message.success('Business details updated');
      } else {
        // Create new (self-service)
        const payload = { ...values };
        const res = await fetch(`${apiBase}/my/business-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload)
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to create business details');
        message.success('Business details saved');
      }
      await loadBusiness();
    } catch (e) {
      if (e?.errorFields) return; // antd validation errors
      message.error(e.message || 'Error saving business details');
    } finally {
      setLoading(false);
    }
  };

  // ===== Security (Password / 2FA) moved here =====
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/auth/2fa`, { headers: { ...authHeaders() } });
        const data = await res.json().catch(() => ({}));
        if (res.ok) setTwoFA({ enabled: !!data.enabled, secret: null, backupCodes: data.backupCodes || [] });
      } catch {}
    })();
  }, []);

  const setup2FA = async () => {
    try {
      const res = await fetch(`${apiBase}/auth/2fa/setup`, { method: 'POST', headers: { ...authHeaders() } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to prepare 2FA');
      setTwoFA({ enabled: false, secret: body.secret, backupCodes: body.backupCodes || [] });
      message.success('2FA setup: secret generated');
    } catch (e) {
      message.error(e.message || 'Failed to setup 2FA');
    }
  };

  const enable2FA = async () => {
    try {
      const { code } = await twoFAForm.validateFields();
      if (!/^\d{6}$/.test(code)) return message.error('Enter a 6-digit code');
      const res = await fetch(`${apiBase}/auth/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ code })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to enable 2FA');
      setTwoFA((prev) => ({ ...prev, enabled: true }));
      message.success('2FA enabled');
      twoFAForm.resetFields();
    } catch {}
  };

  const disable2FA = () => {
    Modal.confirm({
      title: 'Disable 2FA',
      content: 'Are you sure you want to disable two-factor authentication?',
      okText: 'Disable', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await fetch(`${apiBase}/auth/2fa/disable`, { method: 'POST', headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'Failed to disable 2FA');
          setTwoFA({ enabled: false, secret: null, backupCodes: [] });
          message.success('2FA disabled');
        } catch (e) {
          message.error(e.message || 'Failed to disable 2FA');
        }
      }
    });
  };

  const generateBackupCodes = async () => {
    try {
      const res = await fetch(`${apiBase}/auth/2fa/regenerate-codes`, { method: 'POST', headers: { ...authHeaders() } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to regenerate backup codes');
      const codes = body.backupCodes || [];
      setTwoFA((prev) => ({ ...prev, backupCodes: codes }));
      Modal.success({
        title: 'Backup Codes Generated',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>Store these codes somewhere safe.</div>
            <List size="small" bordered dataSource={codes} renderItem={(c) => <List.Item><code>{c}</code></List.Item>} />
          </div>
        )
      });
    } catch (e) {
      message.error(e.message || 'Failed to regenerate codes');
    }
  };

  return (
    <div className="abo-content" style={{ display: 'grid', gap: 16 }}>
      <UserProfile />

      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Business Details</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" onClick={loadBusiness} loading={loading}>Refresh</Button>
              {!editMode && (
                <Button size="small" type="primary" onClick={() => setEditMode(true)}>
                  {biz ? 'Edit' : 'Add'}
                </Button>
              )}
            </div>
          </div>
        }
        hoverable
        style={{ borderRadius: 14 }}
        bodyStyle={{ padding: 20 }}
        loading={loading}
      >
        {!editMode ? (
          biz ? (
            <div style={{ fontSize: 13, color: '#334155' }}>
              <div style={{ marginBottom: 6 }}>
                <strong>Company:</strong> <span style={{ marginLeft: 6 }}>{biz.company_name || '—'}</span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Industry:</strong> <span style={{ marginLeft: 6, textTransform: 'capitalize' }}>{biz.industry || '—'}</span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Website:</strong> <span style={{ marginLeft: 6 }}>{biz.website ? (<a href={biz.website} target="_blank" rel="noreferrer">{biz.website}</a>) : '—'}</span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Email:</strong> <span style={{ marginLeft: 6 }}>{biz.business_email || '—'}</span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Contact:</strong> <span style={{ marginLeft: 6 }}>{biz.contact || '—'}</span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Address:</strong>
                <div style={{ marginLeft: 6 }}>{biz.address || '—'}</div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#64748b' }}>No business details found. Click Add to create your business profile.</div>
          )
        ) : (
          <Form form={form} layout="vertical" onFinish={async () => { await saveBusiness(); setEditMode(false); }}>
            <Form.Item name="company_name" label="Company Name" rules={[{ required: true, message: 'Please enter company name' }]}> 
              <Input placeholder="e.g., Acme Inc." />
            </Form.Item>

            <Form.Item name="industry" label="Industry">
              <Select
                placeholder="Select industry"
                allowClear
                showSearch
                options={[
                  { label: 'Technology', value: 'technology' },
                  { label: 'Healthcare', value: 'healthcare' },
                  { label: 'Finance', value: 'finance' },
                  { label: 'E-commerce', value: 'ecommerce' },
                  { label: 'Education', value: 'education' },
                  { label: 'Manufacturing', value: 'manufacturing' },
                  { label: 'Real Estate', value: 'real_estate' },
                  { label: 'Consulting', value: 'consulting' },
                  { label: 'Marketing', value: 'marketing' },
                  { label: 'Other', value: 'other' },
                ]}
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item name="website" label="Website URL" rules={[{ type: 'url', message: 'Please enter a valid URL' }]}>
              <Input placeholder="https://example.com" />
            </Form.Item>

            <Form.Item name="business_email" label="Business Email" rules={[{ type: 'email', message: 'Please enter a valid email' }]}>
              <Input placeholder="contact@company.com" />
            </Form.Item>

            <Form.Item name="contact" label="Contact Number">
              <Input placeholder="+1 (555) 123-4567" />
            </Form.Item>

            <Form.Item name="address" label="Business Address">
              <Input.TextArea rows={3} placeholder="Enter business address" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Button style={{ marginRight: 8 }} onClick={() => { form.resetFields(); setEditMode(false); }} disabled={loading}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>Save</Button>
            </Form.Item>
          </Form>
        )}
      </Card>

      {/* Security: Two-Factor Authentication */}
      <Card style={{ borderRadius: 14 }} title="Two-Factor Authentication">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            Status: {twoFA.enabled ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>}
          </div>
          {!twoFA.enabled ? (
            <>
              <Space>
                <Button onClick={setup2FA}>Generate secret</Button>
              </Space>
              {twoFA.secret && (
                <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, wordBreak: 'break-all', marginTop: 8 }}>
                  <div><strong>Secret:</strong> {twoFA.secret}</div>
                </div>
              )}
              <Form layout="inline" form={twoFAForm} onFinish={enable2FA} style={{ marginTop: 8 }}>
                <Form.Item name="code" rules={[{ required: true, message: 'Enter 6-digit code' }]}> 
                  <Input placeholder="123456" maxLength={6} style={{ width: 140 }} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" onClick={enable2FA}>Enable 2FA</Button>
                </Form.Item>
              </Form>
            </>
          ) : (
            <>
              <Space wrap>
                <Button onClick={generateBackupCodes}>Generate backup codes</Button>
                <Button danger onClick={disable2FA}>Disable 2FA</Button>
              </Space>
              {!!twoFA.backupCodes?.length && (
                <div style={{ marginTop: 8 }}>
                  <Text strong>Existing backup codes:</Text>
                  <List size="small" bordered style={{ marginTop: 6 }} dataSource={twoFA.backupCodes} renderItem={(c) => <List.Item><code>{c}</code></List.Item>} />
                </div>
              )}
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
;
export default Profile;
